import React, { FC } from 'react';

import { useUnit } from 'effector-react/ssr';

import clsx from 'clsx';

import { ProductsListFilters } from '@domains/products-catalog';

import { $productsList, setFilters } from '@stores/products-list';

import { isValueSet } from '@utils/guards';
import { KopecksIso } from '@utils/isomorph';

import styles from './CategoryPriceRangeFilter.module.scss';

import { CategoryPriceRangeInput } from '../CategoryPriceRangeInput/CategoryPriceRangeInput';

type MinMaxKeys = keyof Pick<ProductsListFilters, 'max_price' | 'min_price'>;

export type PriceRangeFilterVariant = 'primary' | 'secondary';

export type PriceRangeFilterProps = {
    variant?: PriceRangeFilterVariant;
};

const variantToClassMap: Record<PriceRangeFilterVariant, string> = {
    primary: styles.isVariantPrimary,
    secondary: styles.isVariantSecondary,
};

export const CategoryPriceRangeFilter: FC<PriceRangeFilterProps> = (props) => {
    const { variant = 'primary' } = props;

    const { filters, availableFilters } = useUnit($productsList);
    const setFiltersFn = useUnit(setFilters);

    const availableMinNumber = isValueSet(availableFilters.min_price)
        ? KopecksIso.get(availableFilters.min_price)
        : undefined;

    const availableMaxNumber = isValueSet(availableFilters.max_price)
        ? KopecksIso.get(availableFilters.max_price)
        : undefined;

    const formatChangedValue = (value: number | undefined): FeipApi.Kopecks | undefined =>
        isValueSet(value) ? KopecksIso.from(value) : undefined;

    const setMinOrMaxFilter = (field: MinMaxKeys, value: number | undefined) => {
        setFiltersFn({ ...filters, [field]: formatChangedValue(value) });
    };

    const handleChange = (field: MinMaxKeys) => (value: number | undefined) =>
        setMinOrMaxFilter(field, value);

    const handleBlur = (blurredField: MinMaxKeys) => () => {
        const minOrMax = filters[blurredField];
        if (!isValueSet(minOrMax)) return;
        const minOrMaxValue = KopecksIso.get(minOrMax);

        //region Reset to available price min & max inputs
        if (isValueSet(availableMinNumber) && availableMinNumber > minOrMaxValue) {
            setMinOrMaxFilter(blurredField, availableMinNumber);
        }

        if (isValueSet(availableMaxNumber) && minOrMaxValue > availableMaxNumber) {
            setMinOrMaxFilter(blurredField, availableMaxNumber);
        }
        //endregion Reset to available price min & max inputs

        // Swap inputs value if min > max
        if (
            isValueSet(filters.min_price) &&
            isValueSet(filters.max_price) &&
            filters.min_price > filters.max_price
        ) {
            const newMin: FeipApi.Kopecks = filters.max_price;
            let newMax: FeipApi.Kopecks = filters.min_price;

            if (isValueSet(availableMaxNumber) && KopecksIso.get(newMax) > availableMaxNumber) {
                newMax = KopecksIso.from(availableMaxNumber);
            }

            setFiltersFn({
                ...filters,
                max_price: newMax,
                min_price: newMin,
            });
        }
    };

    return (
        <div className={clsx(styles.root, variantToClassMap[variant])}>
            {variant === 'secondary' && <h6 className={styles.title}>Цена</h6>}
            <CategoryPriceRangeInput
                value={filters.min_price}
                prefix="от"
                placeholderValue={availableFilters.min_price}
                variant={variant}
                className={styles.minPriceInput}
                onChange={handleChange('min_price')}
                onBlur={handleBlur('min_price')}
                data-testid="filter-price-from"
            />
            <CategoryPriceRangeInput
                value={filters.max_price}
                prefix="до"
                placeholderValue={availableFilters.max_price}
                variant={variant}
                className={styles.maxPriceInput}
                onChange={handleChange('max_price')}
                onBlur={handleBlur('max_price')}
                data-testid="filter-price-to"
            />
        </div>
    );
};
